import centralAxiosInstance from '../centralAxiosInstance';
import { resolveApiOrigin } from '../apiOrigin';

const API_ORIGIN = resolveApiOrigin();
const BRANDING_CACHE_PREFIX = 'dth_public_branding:';
const inflight = new Map();

export function absoluteAssetUrlForStoredPath(storedPath) {
  if (!storedPath || !String(storedPath).trim()) return '';
  const path = String(storedPath).trim();
  if (/^https?:\/\//i.test(path)) return path;
  return path.startsWith('/') ? `${API_ORIGIN}${path}` : `${API_ORIGIN}/api/v1/${path}`;
}

function cacheKey(tenantKey) {
  return `${BRANDING_CACHE_PREFIX}${String(tenantKey).trim().toLowerCase()}`;
}

function slimBranding(branding) {
  if (!branding || typeof branding !== 'object') return null;
  return {
    id: branding.id ?? null,
    name: branding.name ?? null,
    primaryColor: branding.primaryColor ?? null,
    secondaryColor: branding.secondaryColor ?? null,
    backgroundColor: branding.backgroundColor ?? null,
    tenantPhoto: branding.tenantPhoto ?? null,
  };
}

/** Read cached public branding for instant login paint (survives logout hard reload). */
export function getCachedPublicBranding(tenantKey) {
  if (!tenantKey || typeof window === 'undefined' || !window.sessionStorage) return null;
  try {
    const raw = window.sessionStorage.getItem(cacheKey(tenantKey));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
}

/** Persist branding under UUID and/or name keys. */
export function setCachedPublicBranding(branding, extraKeys = []) {
  if (!branding || typeof window === 'undefined' || !window.sessionStorage) return;
  const slim = slimBranding(branding);
  if (!slim) return;
  const keys = new Set(
    [slim.id, slim.name, ...extraKeys]
      .filter((k) => k != null && String(k).trim() !== '')
      .map((k) => String(k).trim())
  );
  // Keep a previously cached logo if a later payload omits tenantPhoto.
  if (!slim.tenantPhoto) {
    for (const k of keys) {
      const prev = getCachedPublicBranding(k);
      if (prev?.tenantPhoto) {
        slim.tenantPhoto = prev.tenantPhoto;
        break;
      }
    }
  }
  try {
    const json = JSON.stringify(slim);
    keys.forEach((k) => window.sessionStorage.setItem(cacheKey(k), json));
  } catch {
    /* ignore quota */
  }
}

/**
 * Public tenant branding (logo + colors + name).
 * {@code tenantKey} may be registry UUID or tenant name (e.g. datahouse).
 */
export async function fetchPublicTenantBrandingById(tenantKey) {
  if (!tenantKey || !String(tenantKey).trim()) {
    return null;
  }
  const key = String(tenantKey).trim();
  if (inflight.has(key)) {
    return inflight.get(key);
  }

  const request = (async () => {
    try {
      const response = await centralAxiosInstance.get(
        `/tenants/${encodeURIComponent(key)}/branding`
      );
      const payload = response?.data ?? null;
      const branding =
        payload?.data && typeof payload.data === 'object' && payload.data.primaryColor !== undefined
          ? payload.data
          : payload;
      if (branding && typeof branding === 'object') {
        setCachedPublicBranding(branding, [key]);
        return branding;
      }
      return null;
    } catch {
      return null;
    } finally {
      inflight.delete(key);
    }
  })();

  inflight.set(key, request);
  return request;
}
